import React, { createContext, useContext, useEffect, useState } from 'react'
import { User } from '../types'
import { telegramAuth, getMe } from '../api/auth'
import { updateMe } from '../api/users'
import { useTelegram } from '../hooks/useTelegram'

interface AuthContextValue {
  user: User | null
  isLoading: boolean
  isAdmin: boolean
  isSuperadmin: boolean
  setLanguage: (lang: 'ru' | 'uz') => Promise<void>
  reload: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue>({
  user: null, isLoading: true, isAdmin: false, isSuperadmin: false,
  setLanguage: async () => {}, reload: async () => {},
})

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { initData, ready, expand } = useTelegram()
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  async function init() {
    setIsLoading(true)
    try {
      // Try existing token first
      let token = localStorage.getItem('jwt')
      if (!token) {
        if (!initData) {
          // Dev fallback: no Telegram context — skip auth
          setIsLoading(false)
          return
        }
        const res = await telegramAuth(initData)
        token = res.access_token
        localStorage.setItem('jwt', token)
      }
      const me = await getMe()
      setUser(me)
    } catch {
      localStorage.removeItem('jwt')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    expand()
    ready()
    init()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  async function setLanguage(lang: 'ru' | 'uz') {
    if (!user) return
    const updated = await updateMe({ language: lang })
    setUser(updated)
  }

  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin'
  const isSuperadmin = user?.role === 'superadmin'

  return (
    <AuthContext.Provider value={{ user, isLoading, isAdmin, isSuperadmin, setLanguage, reload: init }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  return useContext(AuthContext)
}
